/**
 * Simple Markdown to HTML parser for chat messages
 * Handles common markdown formatting without external dependencies
 */

export function parseMarkdown(text: string): string {
  if (!text) return '';

  // Escape HTML to prevent XSS
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  // Headers (h3 for ###, h4 for ####)
  html = html.replace(/^### (.+)$/gm, '<h3 class="chat-heading-3">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h3 class="chat-heading-2">$1</h3>');
  html = html.replace(/^# (.+)$/gm, '<h2 class="chat-heading-1">$1</h2>');

  // Process lists with a more comprehensive approach
  // First, handle numbered lists with their content (including sub-items)
  const lines = html.split('\n');
  const processedLines: string[] = [];
  let inNumberedList = false;
  let currentListItem = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const numberedMatch = line.match(/^(\d+)\.\s+(.+)$/);
    const bulletMatch = line.match(/^(\s*)-\s+(.+)$/);

    if (numberedMatch) {
      // Start of a numbered list item
      if (currentListItem) {
        // Save previous list item
        processedLines.push(`<li class="numbered">${currentListItem}</li>`);
      }
      currentListItem = numberedMatch[2];
      inNumberedList = true;
    } else if (inNumberedList && bulletMatch) {
      // Sub-item within a numbered list
      if (currentListItem) {
        // Use a fixed indentation for sub-items (not based on spaces)
        currentListItem += `\n<span class="sub-item" style="display: block; margin-left: 20px">• ${bulletMatch[2]}</span>`;
      }
    } else if (inNumberedList && line.trim() === '') {
      // Empty line - check if next line continues the list
      const nextLine = i + 1 < lines.length ? lines[i + 1] : '';
      if (nextLine.match(/^\d+\.\s+/) || nextLine.match(/^\s*-\s+/)) {
        // Next line is a list item, continue
        continue;
      } else if (nextLine.trim() === '') {
        // Multiple empty lines, end the list
        if (currentListItem) {
          processedLines.push(`<li class="numbered">${currentListItem}</li>`);
          currentListItem = '';
          inNumberedList = false;
        }
        processedLines.push(line);
      }
    } else if (inNumberedList && !line.match(/^\d+\.\s+/) && !line.startsWith('   ')) {
      // Non-list line that's not indented, end current item and list
      if (currentListItem) {
        processedLines.push(`<li class="numbered">${currentListItem}</li>`);
        currentListItem = '';
        inNumberedList = false;
      }
      processedLines.push(line);
    } else {
      // Regular line or continuation
      if (inNumberedList && line.startsWith('   ')) {
        // Continuation of current list item (indented)
        currentListItem += '\n' + line.trim();
      } else if (!inNumberedList) {
        processedLines.push(line);
      }
    }
  }

  // Don't forget the last item
  if (currentListItem) {
    processedLines.push(`<li class="numbered">${currentListItem}</li>`);
  }

  html = processedLines.join('\n');

  // Handle standalone bullet lists (not within numbered lists)
  html = html.replace(/^-\s+(.+)$/gm, '<li class="bullet">$1</li>');

  // Bold text - after lists to allow bold within list items
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic text
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

  // Wrap consecutive numbered <li> elements in <ol>
  // More robust pattern that handles multi-line list items
  html = html.replace(/(<li class="numbered">[\s\S]*?<\/li>(?:\s*(?=<li class="numbered">))?)+/g, function(match) {
    return '<ol class="chat-list-ordered">' + match + '</ol>';
  });

  // Wrap consecutive bullet <li> elements in <ul>
  html = html.replace(/(<li class="bullet".*?>.*?<\/li>\s*)+/g, function(match) {
    return '<ul class="chat-list">' + match + '</ul>';
  });

  // Line breaks - preserve structure
  const sections = html.split('\n\n');
  html = sections
    .map(section => {
      // Don't wrap if it's already an HTML element
      if (section.trim().startsWith('<h') ||
          section.trim().startsWith('<ul') ||
          section.trim().startsWith('<ol') ||
          section.trim().startsWith('<pre')) {
        return section;
      }
      // For regular text, preserve single line breaks within the section
      if (section.trim()) {
        // Check if this section contains multiple lines that should stay together
        const lines = section.split('\n').filter(line => line.trim());
        if (lines.length > 1) {
          // Multiple lines - preserve breaks between them
          return `<div class="chat-text-block">${section.replace(/\n/g, '<br>')}</div>`;
        } else {
          // Single line - wrap as paragraph
          return `<p>${section}</p>`;
        }
      }
      return '';
    })
    .filter(section => section)
    .join('\n');

  // Code blocks (basic support)
  html = html.replace(/```([\s\S]*?)```/g, '<pre class="chat-code-block"><code>$1</code></pre>');
  html = html.replace(/`([^`]+)`/g, '<code class="chat-inline-code">$1</code>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  return html;
}

/**
 * CSS styles for formatted chat messages
 * Add this to your component's style section
 */
export const markdownStyles = `
  .chat-message-formatted {
    word-wrap: break-word;
    line-height: 1.5;
  }

  .chat-message-formatted h2 {
    font-size: 1.3em;
    font-weight: 600;
    margin: 0.5em 0;
    color: #333;
  }

  .chat-message-formatted h3 {
    font-size: 1.1em;
    font-weight: 600;
    margin: 0.5em 0;
    color: #444;
  }

  .chat-message-formatted p {
    margin: 0.5em 0;
  }

  .chat-message-formatted ul.chat-list {
    margin: 0.5em 0;
    padding-left: 1.5em;
    list-style-type: disc;
  }

  .chat-message-formatted ul.chat-list li {
    margin: 0.25em 0;
    list-style-type: disc;
  }

  .chat-message-formatted strong {
    font-weight: 600;
    color: #222;
  }

  .chat-message-formatted em {
    font-style: italic;
  }

  .chat-message-formatted code.chat-inline-code {
    background-color: #f5f5f5;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 0.9em;
  }

  .chat-message-formatted pre.chat-code-block {
    background-color: #f5f5f5;
    padding: 10px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 0.5em 0;
  }

  .chat-message-formatted a {
    color: #0066cc;
    text-decoration: none;
  }

  .chat-message-formatted a:hover {
    text-decoration: underline;
  }
`;